import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { NextDeliverySlots, Product, Restaurant, RestaurantStats, Session, Team } from '../restaurant-manager.models';

@Injectable({
    providedIn: 'root'
})
export class RestaurantService {
    selectedRestaurant: BehaviorSubject<Restaurant> = new BehaviorSubject<Restaurant>(null);

    constructor(
        protected apiService: ApiService
    ) {
        this.selectedRestaurant.next(RestaurantService.getFromLocalStorage());
    }

    static getFromLocalStorage(): Restaurant {
        const restaurant = JSON.parse(localStorage.getItem('selected_restaurant'));
        return restaurant ? new Restaurant(restaurant) : null;
    }

    save(): void {
        localStorage.setItem('selected_restaurant', JSON.stringify(this.selectedRestaurant.value));
    }

    list(): Observable<Restaurant[]> {
        return this.apiService.get<Restaurant[]>('/restaurants', false).pipe(map((result: Restaurant[]) => {
            const restaurants: Restaurant[] = result.map(r => new Restaurant(r));
            if (restaurants.length > 0) {
                if (this.selectedRestaurant.value) {
                    const restaurant = restaurants.find(r => r.id === RestaurantService.getFromLocalStorage().id);
                    if (restaurant) {
                        this.selectRestaurant(restaurant);
                    } else {
                        this.selectRestaurant(null);
                    }
                }
            }
            return restaurants;
        }));
    }

    isProductAvailable(product: Product): boolean {
        for (const stock of product.stocks) {
            if (this.selectedRestaurant.value && stock.restaurant.id === this.selectedRestaurant.value.id) {
                return stock.hasStock;
            }
        }
        return true;
    }

    selectRestaurant(restaurant: Restaurant): void {
        this.selectedRestaurant.next(restaurant);
        this.save();
    }

    update(restaurantId: string, data: any): Observable<Restaurant> {
        return this.apiService.put<Restaurant>('/restaurants/' + restaurantId, data, true);
    }

    changeStock(restaurantId: string, productId: string, hasStock: boolean): Observable<Restaurant[]> {
        return this.apiService.put<any>('/restaurant-stocks', {
            restaurant: restaurantId,
            product: productId,
            stock: hasStock
        }, true);
    }

    getStats(restaurantId: string, date: string): Observable<RestaurantStats> {
        return this
            .apiService.get<RestaurantStats>('/restaurants/' + restaurantId + '/stats?date=' + date, true)
            .pipe(map(result => new RestaurantStats(result)));
    }

    generateSlots(restaurantId: string): Observable<NextDeliverySlots> {
        return this.apiService.post<NextDeliverySlots>('/delivery/restaurant/' + restaurantId + '/generate', {}, true).pipe(map(nextSlots => {
            return new NextDeliverySlots(nextSlots);
        }));
    }

    /**
    * @deprecated The method should not be used
    */
    getTeams(): Observable<Team[]> {
        return this.apiService.get<Team[]>('/restaurants/' + this.selectedRestaurant.value.id + '/teams', true).pipe(map((teams: any[]) => {
            return teams.map(t => new Team(t));
        }));
    }

}
